/* eslint-disable import/first */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipFormatter,
} from 'recharts';

import { Type } from '@spotify-internal/encore-creator-web';
import chartStyles from '../../../styles/charts.module.scss';
import styles from './index.module.scss';
import { LoadingIndicator } from '../../../components/Shared/LoadingIndicator';

import { loadEpisodePerformance } from '../../../store/episodePerformance';
import {
  LOADING,
  ERROR,
  selectEpisodePerformance,
  selectEpisodePerformanceStatus,
} from '../../../store/selectors';
import { formatDisplayTimestamp } from '../../../lib/Formatter';
import { ReduxState, EpisodePerformance as TEpisodePerformance } from '../../../types';
import {
  FormattedMessage,
  injectIntl,
  IntlShape,
  FormattedNumber,
  defineMessage,
} from 'react-intl';

type Props = {
  episodeId: string;
  status?: 'LOADING' | 'ERROR';
  episodePerformance?: TEpisodePerformance;
  loadEpisodePerformance: typeof loadEpisodePerformance;
  intl: IntlShape;
};

class EpisodePerformance extends PureComponent<Props> {
  componentDidMount() {
    this.props.loadEpisodePerformance(this.props.episodeId);
  }

  get averageCompletionTime() {
    const { seconds } = this.props.episodePerformance!.medianCompletion;
    return formatDisplayTimestamp(seconds, this.props.intl);
  }

  renderEpisodePerformanceMetadata() {
    const { percentiles } = this.props.episodePerformance!;
    return (
      <div className={classNames([styles.container, styles['metadata-container']])}>
        {this.renderMetadataItem(
          this.props.intl.formatMessage(
            defineMessage({
              id: 'episode.episode-performance.chart-average-listen-total',
              description: 'average listens title',
              defaultMessage: 'Average Listen',
            }),
          ),
          this.averageCompletionTime,
        )}
        {this.renderMetadataItem(
          this.props.intl.formatMessage(
            defineMessage({
              id: 'episode.episode-performance.chart-first-quartile',
              description: 'average listens in 1st quartile of episode title',
              defaultMessage: '1st Quartile',
            }),
          ),
          `${this.props.intl.formatNumber((percentiles['25'] / 100) as number, {
            style: 'percent',
          })}`,
        )}
        {this.renderMetadataItem(
          this.props.intl.formatMessage(
            defineMessage({
              id: 'episode.episode-performance.chart-second-quartile',
              description: 'average listens in 2nd quartile of episode title',
              defaultMessage: '2nd Quartile',
            }),
          ),
          `${this.props.intl.formatNumber((percentiles['50'] / 100) as number, {
            style: 'percent',
          })}`,
        )}
        {this.renderMetadataItem(
          this.props.intl.formatMessage(
            defineMessage({
              id: 'episode.episode-performance.chart-third-quartile',
              description: 'average listens in 3rd quartile of episode title',
              defaultMessage: '3rd Quartile',
            }),
          ),
          `${this.props.intl.formatNumber((percentiles['75'] / 100) as number, {
            style: 'percent',
          })}`,
        )}
        {this.renderMetadataItem(
          this.props.intl.formatMessage(
            defineMessage({
              id: 'episode.episode-performance.chart-complete',
              description: 'average listens of the complete episode title',
              defaultMessage: 'Complete',
            }),
          ),
          `${this.props.intl.formatNumber((percentiles['100'] / 100) as number, {
            style: 'percent',
          })}`,
        )}
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderMetadataItem(label: string, value: string) {
    return (
      <div className={styles['metadata-item']}>
        <Type
          as="p"
          variant={Type.cta3}
          color="gray60"
          className={styles['metadata-item-label']}
          condensed
        >
          {label}
        </Type>
        <Type as="h1" variant={Type.heading4} condensed>
          {value}
        </Type>
      </div>
    );
  }

  renderChart = () => (
    <ResponsiveContainer height={350}>
      <AreaChart
        className={chartStyles.chart}
        data={this.props.episodePerformance!.performanceData}
        margin={{ top: 25, right: 10, left: -20, bottom: 0 }}
      >
        <XAxis axisLine={false} dataKey="timestamp" type="category" tickLine={false} />
        <YAxis
          unit={'%'}
          ticks={[0, 50, 100].map(tick => this.props.intl.formatNumber(tick))}
          axisLine={false}
          tickLine={false}
        />
        <CartesianGrid vertical={false} />
        <Tooltip
          isAnimationActive={false}
          itemStyle={{ color: 'unset' }}
          wrapperStyle={{
            borderRadius: '5px',
            boxShadow: '0 3px 8px 0 rgba(0, 0, 0, 0.2)',
            borderColor: 'white',
          }}
          formatter={tooltipFormatter}
          separator=" "
        />
        <Area
          type="basis"
          dataKey="retention"
          stackId="1"
          stroke="#1dd660"
          fill="#a5efbf"
          strokeWidth={2}
          activeDot={{ strokeWidth: 3, r: 7 }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  render() {
    if (this.props.status === ERROR) {
      return <span data-qa="episode-performance-error" />;
    }

    if (!this.props.episodePerformance && this.props.status !== LOADING) {
      return null;
    }

    return (
      <div data-qa="episode-performance-chart">
        <Type as="h1" variant={Type.heading3}>
          <FormattedMessage
            id="episode.episode-performance.title"
            defaultMessage="Episode Performance"
          />
        </Type>
        <div style={{ width: '100%', height: '350px' }}>
          {this.props.status === LOADING ? <LoadingIndicator /> : this.renderChart()}
        </div>
        {this.props.episodePerformance && this.renderEpisodePerformanceMetadata()}
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    episodePerformance: selectEpisodePerformance(state),
    status: selectEpisodePerformanceStatus(state),
  };
}

const actions = { loadEpisodePerformance };

export const ConnectedEpisodePerformance = connect(
  mapStateToProps,
  actions,
)(injectIntl(EpisodePerformance));

/* istanbul ignore next */
const tooltipFormatter: TooltipFormatter = (value, key) => {
  return (
    <div>
      <span className={chartStyles['audience-tooltip-label']}>
        <FormattedMessage id={`episode.episode-performance.tooltip-${key}`} defaultMessage={key} />
      </span>
      <span className={chartStyles['audience-tooltip-value']}>
        <FormattedNumber
          value={(value as number) / 100}
          style="percent"
          maximumSignificantDigits={4}
        />
      </span>
    </div>
  );
};
